/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth2 authorization code flow.
 *
 * Generates a cryptographic code_verifier, hashes it to a code_challenge (S256),
 * and stores it in localStorage for the token exchange step.
 *
 * localStorage is used instead of localStorage because on native platforms
 * the OAuth flow opens in a system browser (Custom Chrome Tab). The app may
 * restart when the deep link callback fires, so storage must survive that.
 * Values are single-use and deleted immediately after consumption.
 */

const VERIFIER_KEY = 'cairn_pkce_verifier'
const STATE_KEY = 'cairn_oauth_state'

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function generateRandomString(length: number): string {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return base64UrlEncode(array.buffer)
}

export async function generatePkce(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const codeVerifier = generateRandomString(32)
  const encoder = new TextEncoder()
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(codeVerifier))
  const codeChallenge = base64UrlEncode(digest)
  return { codeVerifier, codeChallenge }
}

export function storeVerifier(verifier: string): void {
  localStorage.setItem(VERIFIER_KEY, verifier)
}

export function retrieveVerifier(): string | null {
  const verifier = localStorage.getItem(VERIFIER_KEY)
  localStorage.removeItem(VERIFIER_KEY)
  return verifier
}

export function generateState(): string {
  const state = generateRandomString(16)
  localStorage.setItem(STATE_KEY, state)
  return state
}

export function validateState(received: string): boolean {
  const expected = localStorage.getItem(STATE_KEY)
  localStorage.removeItem(STATE_KEY)
  return expected !== null && expected === received
}
